import { Link } from "react-router-dom";
import PageContainer, { PageHeader } from "@/components/PageContainer";
import { ArrowRight, Activity, Brain, HeartPulse, LineChart, Layers, ClipboardCheck, Network, Calculator, ClipboardList, LogOut, ShieldAlert, Eye, FileText } from "lucide-react";

const TOOLS = [
  { id: "asam", name: "ASAM Dimension Scorer", tag: "Level of care", icon: Layers,
    desc: "Score the six ASAM Dimensions (0–4). Recommended LOC band based on max severity.", href: "/portal/continuum/tools/asam" },
  { id: "chime", name: "CHIME Domain Audit", tag: "Treatment plan", icon: ClipboardCheck,
    desc: "Audit progress across Connectedness, Hope, Identity, Meaning, and Empowerment.", href: "/portal/continuum/tools/chime" },
  { id: "framework_map", name: "Framework Interaction Map", tag: "Fidelity", icon: Network,
    desc: "Which of the 9 core frameworks are active for this patient. Coverage % + gaps.", href: "/portal/continuum/tools/framework-map" },
  { id: "safet", name: "SAFE-T Suicide Risk", tag: "Safety", icon: ShieldAlert,
    desc: "Five-step Suicide Assessment: risk, protective, inquiry, level, intervention.", href: "/portal/continuum/tools/safet" },
  { id: "phq9", name: "PHQ-9", tag: "Depression", icon: Brain,
    desc: "Standardized 9-item depression measure. Total 0–27 with clinical bands.", href: "/portal/continuum/tools/phq9" },
  { id: "gad7", name: "GAD-7", tag: "Anxiety", icon: Activity,
    desc: "Standardized 7-item generalized anxiety measure. Total 0–21.", href: "/portal/continuum/tools/gad7" },
  { id: "ors", name: "Outcome Rating Scale (ORS)", tag: "Outcomes · FIT", icon: LineChart,
    desc: "Ultra-brief 4-item weekly outcome measure. Every session.", href: "/portal/continuum/tools/ors" },
  { id: "srs", name: "Session Rating Scale (SRS)", tag: "Alliance · FIT", icon: HeartPulse,
    desc: "Ultra-brief 4-item alliance measure at end of every session.", href: "/portal/continuum/tools/srs" },
  { id: "screening_battery", name: "Screening Battery Scorer", tag: "Intake", icon: ClipboardList,
    desc: "Enter PHQ-9, GAD-7, AUDIT, DAST, PCL-5 totals — get an intake risk profile.", href: "/portal/continuum/tools/screening" },
  { id: "discharge_readiness", name: "Discharge Readiness Checker", tag: "Transitions", icon: LogOut,
    desc: "12-item checklist covering ASAM 5-6, recovery capital, aftercare, warm handoff.", href: "/portal/continuum/tools/discharge" },
  { id: "mse", name: "Mental Status Exam", tag: "Clinical", icon: Eye,
    desc: "10-domain MSE with select-list for each domain. Documented in one screen.", href: "/portal/continuum/tools/mse" },
  { id: "cognitive", name: "Cognitive Screen", tag: "Neurocog", icon: Brain,
    desc: "SLUMS-inspired 30-point cognitive screen with education-adjusted bands.", href: "/portal/continuum/tools/cognitive" },
  { id: "staffing", name: "Staffing Calculator", tag: "Operations", icon: Calculator,
    desc: "Enter census by LOC. Output FTE required per role using clinical-anchored ratios.", href: "/portal/continuum/tools/staffing" },
  { id: "reading_level", name: "Reading Level Analyzer", tag: "Materials", icon: FileText,
    desc: "Paste treatment materials. Get Flesch-Kincaid grade level against a 6–8 target.", href: "/portal/continuum/tools/reading-level" },
];

export default function ToolsIndex() {
  return (
    <PageContainer testid="tools-index" wide>
      <PageHeader
        overline="Portal · Interactive Tools"
        title="Assessment, safety, and operational tools."
        subtitle="Score, save, and review. Every submission is stored to your history and mapped back to the Continuum's non-negotiables (MBC, FIT, ASAM)."
        actions={
          <Link to="/portal/continuum/tools/history" className="btn btn-secondary" data-testid="tools-history-link">
            View my history
          </Link>
        }
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tools-grid">
        {TOOLS.map((t) => {
          const Icon = t.icon;
          return (
            <Link key={t.id} to={t.href} className="card card-hover p-6 block" data-testid={`tool-card-${t.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-md bg-teal-light flex items-center justify-center">
                  <Icon size={18} strokeWidth={2} className="text-teal" />
                </div>
                <span className="badge badge-neutral">{t.tag}</span>
              </div>
              <h3 className="text-navy font-semibold text-lg mb-2">{t.name}</h3>
              <p className="text-sm text-ink-muted leading-relaxed mb-4">{t.desc}</p>
              <div className="text-sm text-teal font-medium inline-flex items-center gap-1">
                Open tool <ArrowRight size={14} strokeWidth={2} />
              </div>
            </Link>
          );
        })}
      </div>
    </PageContainer>
  );
}
